import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

interface InspectionBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InspectionBookingModal = ({ open, onOpenChange }: InspectionBookingModalProps) => {
  const [step, setStep] = useState<"pricing" | "booking" | "confirmation">("pricing");
  const [houseSize, setHouseSize] = useState<string>("");
  const [province, setProvince] = useState<string>("");
  const [showPrice, setShowPrice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    streetAddress: "",
    city: "",
    postalCode: "",
    desiredDate: "",
  });

  const getPrice = () => {
    if (!houseSize || !province) return null;
    
    if (province === "ontario") {
      return houseSize === "small" ? "$835.00" : "$915.00";
    } else if (province === "alberta") {
      return houseSize === "small" ? "$835.00" : "$1,150.00";
    }
    return null;
  };

  const handleSeePricing = () => {
    if (!houseSize || !province) {
      toast({
        title: "Please make selections",
        description: "Please select both house size and province.",
        variant: "destructive",
      });
      return;
    }
    setShowPrice(true);
  };

  const handleScheduleInspection = () => {
    setStep("booking");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all required fields are filled
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phone || !formData.streetAddress || !formData.city || 
        !formData.postalCode || !formData.desiredDate) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Construct webhook data with all form fields and calculated values
      const fullAddress = `${formData.streetAddress}, ${formData.city}, ${province === "ontario" ? "Ontario" : "Alberta"}, ${formData.postalCode}`;
      const webhookData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        streetAddress: formData.streetAddress,
        city: formData.city,
        province: province === "ontario" ? "Ontario" : "Alberta",
        postalCode: formData.postalCode,
        fullAddress: fullAddress,
        desiredDate: formData.desiredDate,
        houseSize: houseSize === "small" ? "0-3,000 sq ft" : "3,001-6,000 sq ft",
        price: getPrice(),
        timestamp: new Date().toISOString(),
        source: "House Beat Inspection Booking"
      };

      // Send data to Make.com webhook
      const response = await fetch("https://hook.us2.make.com/nkf1j6lcig3jb57qzqbxen0emtd32l31", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      // Check if webhook call was successful
      if (response.ok) {
        // Trigger confetti animation (dynamic import)
        const confetti = (await import("canvas-confetti")).default;
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        // Show confirmation screen
        setStep("confirmation");
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting inspection request:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset on close
    setTimeout(() => {
      setStep("pricing");
      setShowPrice(false);
      setHouseSize("");
      setProvince("");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        streetAddress: "",
        city: "",
        postalCode: "",
        desiredDate: "",
      });
    }, 200);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        {step === "pricing" ? (
          <>
            <DialogHeader>
              <DialogTitle>Book Your Home Inspection</DialogTitle>
              <DialogDescription>
                Select your house size and location to see pricing
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="space-y-3">
                <Label className="text-base font-semibold">House Size</Label>
                <RadioGroup value={houseSize} onValueChange={setHouseSize}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="small" id="small" />
                    <Label htmlFor="small" className="font-normal cursor-pointer">
                      0-3,000 square feet
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="large" id="large" />
                    <Label htmlFor="large" className="font-normal cursor-pointer">
                      3,001 - 6,000 square feet
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold">Province</Label>
                <RadioGroup value={province} onValueChange={setProvince}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ontario" id="ontario" />
                    <Label htmlFor="ontario" className="font-normal cursor-pointer">
                      Ontario
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="alberta" id="alberta" />
                    <Label htmlFor="alberta" className="font-normal cursor-pointer">
                      Alberta
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-sm text-muted-foreground">
                  Note: Only available in Ontario and Alberta at this time.
                </p>
              </div>

              {showPrice && (
                <div className="bg-accent/10 p-6 rounded-lg border border-border">
                  <div className="text-center space-y-2">
                    <p className="text-4xl font-bold text-foreground">
                      {getPrice()} <span className="text-sm text-muted-foreground">+ applicable taxes</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This price includes a premium inspection covering all aspects of the home's interior and exterior as well as the upload and population of your House Beat account.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {!showPrice ? (
                  <Button onClick={handleSeePricing} className="w-full">
                    See Inspection Price
                  </Button>
                ) : (
                  <Button onClick={handleScheduleInspection} className="w-full">
                    Schedule Inspection
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : step === "booking" ? (
          <>
            <DialogHeader>
              <DialogTitle>Schedule Your Inspection</DialogTitle>
              <DialogDescription>
                Fill out the form below to request your inspection
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="streetAddress">Street Number and Name</Label>
                <Input
                  id="streetAddress"
                  required
                  placeholder="123 Main Street"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    required
                    placeholder="A1A 1A1"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="province">Province</Label>
                <Input
                  id="province"
                  value={province === "ontario" ? "Ontario" : "Alberta"}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="desiredDate">Desired Date</Label>
                <Input
                  id="desiredDate"
                  type="date"
                  required
                  value={formData.desiredDate}
                  onChange={(e) => setFormData({ ...formData, desiredDate: e.target.value })}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Request Inspection"}
              </Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-center">Request Received!</DialogTitle>
              <DialogDescription className="text-center">
                Your inspection request has been successfully submitted
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="bg-accent/10 p-6 rounded-lg border border-border space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-muted-foreground">Address</p>
                  <p className="text-lg text-foreground">
                    {formData.streetAddress}<br />
                    {formData.city}, {province === "ontario" ? "Ontario" : "Alberta"} {formData.postalCode}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">Price</p>
                    <p className="text-lg text-foreground">
                      {getPrice()} <span className="text-muted-foreground">+ applicable taxes</span>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">Size</p>
                    <p className="text-lg text-foreground">
                      {houseSize === "small" ? "0-3,000 sq ft" : "3,001-6,000 sq ft"}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-muted-foreground">Desired Date</p>
                  <p className="text-lg text-foreground">{formatDate(formData.desiredDate)}</p>
                </div>
              </div>

              <div className="space-y-4 text-center">
                <p className="text-base text-foreground">
                  A House Beat representative will contact you within 24 hours to confirm and collect payment.
                </p>
                <p className="text-sm text-muted-foreground">
                  Email{" "}
                  <a 
                    href="mailto:hello@housebeat.com" 
                    className="text-primary hover:underline"
                  >
                    hello@housebeat.com
                  </a>
                  {" "}with any questions.
                </p>
              </div>

              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
